import CloseCircleSmallIcon from '@assets/icons/close-circle-x-small.svg';
import { Avatar, Size } from '@/components/avatar';
import { createStyles } from '@/contexts/theme-context';
import {
  CursorValue,
  Pressable,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { type FC, useEffect, useRef, useState } from 'react';
import { OrderInboundAssignedEmployee } from './types';
import { TooltipContainer } from '@/components/tooltip-container';
import { TooltipPosition } from '@/enums';

interface Props {
  assignedEmployee: OrderInboundAssignedEmployee;
  avatarSize?: Size;
  containerStyle?: StyleProp<ViewStyle>;
  onPressAvatar?: () => void;
  onUnassignEmployee?: () => void;
}

export const InboundDetailHeaderAssignedEmployee: FC<Props> = ({
  assignedEmployee,
  containerStyle,
  onPressAvatar,
  onUnassignEmployee,
  avatarSize,
}: Props) => {
  const [styles] = useStyles();
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <View
      onPointerEnter={() => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setIsTooltipVisible(true);
      }}
      onPointerLeave={() => {
        timeoutRef.current = setTimeout(() => {
          setIsTooltipVisible(false);
        }, 250);
      }}
      style={[containerStyle]}
      testID="inbound-detail-header-assigned-employee"
    >
      <TooltipContainer
        content={assignedEmployee.name}
        hideClosingLayer
        onClose={() => setIsTooltipVisible(false)}
        pointerEvents="none"
        position={TooltipPosition.Center}
        visible={isTooltipVisible}
      >
        <Pressable
          disabled={!onPressAvatar}
          onPress={onPressAvatar}
          style={styles.avatarContainer}
        >
          <Avatar
            name={assignedEmployee.name}
            size={avatarSize ?? 'small'}
            uuid={assignedEmployee.uuid}
          />
        </Pressable>
      </TooltipContainer>

      {onUnassignEmployee ? (
        <TouchableOpacity
          hitSlop={20}
          onPress={onUnassignEmployee}
          style={styles.closeIcon}
        >
          <CloseCircleSmallIcon />
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  avatarContainer: {
    cursor: 'default' as CursorValue,
  },
  closeIcon: {
    backgroundColor: theme.colors.background,
    borderRadius: 32,
    position: 'absolute',
    right: -5,
    top: -7,
  },
}));
